/**
 * The command layer of the ANONYMOUS surfaces.
 *
 * The prototype's screens are written against one interface, and three implementations fill it:
 * the demo applies the reference's rule in the tab, the internal console calls the authorized
 * routes, and this one calls the two routes a recipient's one-time token authorises and refuses
 * everything else.
 *
 * The refusals are the point. `V07Commands` names every act any screen can perform, and a public
 * page can perform almost none of them — so each is answered with an explicit «this surface cannot
 * do that» rather than with a silent no-op. A public page that appeared to revoke a credential and
 * changed nothing would be exactly the dead button §11 forbids.
 *
 * No command here reads a session, attaches a bearer token, or stores the claim token. The token
 * is passed in from the address by the screen that holds it, used by the request that must carry
 * it, and kept nowhere.
 */
import type { ApiClient } from '../../api/client';
import { isApiError } from '../../api/errors';
import {
  acceptClaim,
  declineClaim,
  recordPublicEngagement,
  recordPublicInteraction,
} from '../../api/publicApi';
import type { AdministrativeUserView, CommandResult, V07Commands } from './commands';
import type { DemoDatabase } from './types';

export interface PublicCommandDeps {
  api: ApiClient;
  /** Screen-local state only — the language choice and the reference's own info boxes. */
  patchLocal: (mutate: (db: DemoDatabase) => void) => void;
  /** Told the public code an acceptance produced, so the page can navigate to it. */
  onAccepted?: ((publicCode: string) => void) | undefined;
}

const NOT_HERE = 'Bu işlem yalnız yetkili iç konsoldan yapılabilir.';

const refuse = async (message = NOT_HERE): Promise<CommandResult> => ({ ok: false, message });

/**
 * An API failure, as the recipient should read it.
 *
 * The backend's own message is used where it has one — it is the sentence the domain chose for
 * that rejection — and a network failure gets the reference's own «tanınmadı» wording rather than
 * a transport error a recipient cannot act on. No code, constraint name or status ever reaches
 * the page (PO 7.8 §3).
 */
function failure(error: unknown): CommandResult {
  if (isApiError(error))
    return {
      ok: false,
      message: error.message,
      ...(error.correlationId ? { correlationId: error.correlationId } : {}),
    };
  return { ok: false, message: 'Bu onay bağlantısı tanınmadı.' };
}

export function publicCommands(deps: PublicCommandDeps): V07Commands {
  return {
    // ---- the recipient's own surface: the two commands the token authorises
    async acceptClaim(input) {
      try {
        const r = await acceptClaim(deps.api, input.token);
        deps.onAccepted?.(r.credential.publicCode);
        return { ok: true, publicCode: r.credential.publicCode };
      } catch (error) {
        return failure(error);
      }
    },

    async declineClaim(input) {
      try {
        const r = await declineClaim(deps.api, input.token);
        return { ok: true, publicCode: r.credential.publicCode };
      } catch (error) {
        return failure(error);
      }
    },

    /*
     * G14 — the recipient's «something is wrong» report.
     *
     * The frozen prototype writes it into the ledger. The backend's `credential_event_type` is a
     * CLOSED enum with no value for a recipient's free text, and adding one is a Product Owner
     * decision (matrix G14; PO 7.7 §6 is the precedent for what adding one costs). The button
     * therefore says what happened to the message instead of showing a green box over a report
     * nobody will ever read.
     */
    async reportClaimIssue() {
      return refuse(
        'Hata bildirimi için kalıcı bir denetim olayı henüz tanımlı değil (G14, Product Owner kararı bekliyor). Bildiriminiz kaydedilmedi; lütfen kurumla doğrudan iletişime geçin.',
      );
    },

    /*
     * Rules 14 and 15: nothing here writes to LinkedIn.
     *
     * The helper's three opened-the-form steps are session telemetry in the reference — no legal
     * value, nothing persisted — so they succeed without a round trip. `api_sync_requested` is the
     * one step that means something durable, and it belongs to an authenticated route; a public
     * page may not reach it and says so.
     */
    async recordLinkedInStep(input) {
      if (input.step === 'api_sync_requested')
        return refuse('LinkedIn senkronizasyon kaydı yalnız yetkili iç konsoldan istenebilir.');
      return { ok: true };
    },

    /*
     * The one measurement an anonymous page IS allowed to report: that the visitor started an
     * action which leaves this page. The route admits only the two «started» kinds, so this cannot
     * grow into a claim that LinkedIn saved or published anything.
     */
    async recordEngagement(input) {
      try {
        await recordPublicEngagement(deps.api, input.code, input.kind);
        return { ok: true };
      } catch (error) {
        /* The member's link has already opened. A measurement that failed changes nothing for them. */
        return failure(error);
      }
    },

    // ---- everything an anonymous surface may not do
    issueCredential: () => refuse(),
    reissueCredential: () => refuse(),
    revokeCredential: () => refuse(),
    resendClaimEmail: () => refuse(),
    previewBulkClaimLinks: () => refuse(),
    createBulkClaimLinks: () => refuse(),
    previewFirstSend: () => refuse(),
    listFirstSendRequests: () => refuse(),
    requestFirstSend: () => refuse(),
    readFirstSendRequest: () => refuse(),
    exportCredentials: () => refuse(),
    /*
     * The measurements this page IS entitled to report: acts it performed itself, named at the
     * level it can see them. The route's allow-list is the same one, so none of them can become a
     * system record or a verified success.
     */
    async recordInteraction(input) {
      try {
        await recordPublicInteraction(deps.api, input.code, input.kind, input.key);
        return { ok: true };
      } catch (error) {
        /* The visitor already got what they asked for; a lost measurement costs them nothing. */
        return failure(error);
      }
    },
    /* One record's history is an internal read. An anonymous page may not have it. */
    readCredentialHistory: async (): Promise<{ ok: boolean; message?: string }> => ({
      ok: false,
      message: NOT_HERE,
    }),
    exportCredentialHistory: () => refuse(),
    readSheetSync: async (): Promise<{ ok: boolean; message?: string }> => ({ ok: false, message: NOT_HERE }),
    saveSheetSync: () => refuse(),
    runSheetSync: () => refuse(),
    correctCredential: () => refuse(),
    adminPublishCredential: () => refuse(),
    updateRecipientEmail: () => refuse(),
    createTemplate: () => refuse(),
    duplicateTemplate: () => refuse(),
    renameTemplate: () => refuse(),
    setTemplateArchived: () => refuse(),
    /* Groups are management, and the public surface manages nothing (migration 028). */
    createCredentialGroup: () => refuse(),
    updateCredentialGroup: () => refuse(),
    deleteCredentialGroup: () => refuse(),
    addCredentialsToGroup: () => refuse(),
    removeCredentialsFromGroup: () => refuse(),
    renameIssuanceBatch: () => refuse(),
    saveDraft: () => refuse(),
    publishRelease: () => refuse(),
    deleteDraft: () => refuse(),
    uploadAsset: () => refuse(),
    uploadFont: () => refuse(),
    applyAssetToTemplate: () => refuse(),
    createBatch: () => refuse(),
    requeueBouncedEmails: () => refuse(),
    updateIssuerProfile: () => refuse(),
    saveEmailTemplate: () => refuse(),
    sendEmailTemplateTest: () => refuse(),
    /* The public page has the whole record it is allowed to have; there is nothing to fill in. */
    hydrateCredential: async () => ({ ok: true }),
    listUsers: async (): Promise<{ ok: boolean; users: AdministrativeUserView[]; message?: string }> => ({
      ok: false,
      users: [],
      message: NOT_HERE,
    }),
    createUser: () => refuse(),
    activateUser: () => refuse(),
    assignRole: () => refuse(),
    disableUser: () => refuse(),

    patchLocal: deps.patchLocal,
    /* There is nothing to re-read: the route's own effect owns this page's single record. */
    refresh: async () => undefined,
  };
}
